import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';

import Api from '../../../services/api.service';
import Helper from '../../../services/helper.service';

import TitleQuotation from './../components/title-quotation';
import Loading from '../../../components/Loading/loading';
import LogoLong from '../../../components/Logos/logo-long';

import * as _ from 'lodash';
import ListOfServices from '../../Reservation/components/list-of-services';
import Button from '../../../components/Buttons/button';
import PersonnalInfoForm from '../../../components/Forms/personnal-info-form';
import Hr from '../../../components/Style/hr';
import H3 from '../../../components/Titles/h3';
import Validator from '../../../services/validator.service';
import DescriptionSplit from '../../../components/Description/description-split';
import I18nComponentChooseLanguage from '../../../components/Widgets/i18n-choose-language';
import SerenityPackCard from '../../../components/Card/serenity-pack-card';

export default function QuotationPage() {
    const [loading, setLoading] = useState(true);
    const { id } = useParams();

    const [loadedQuotation, setQuotation] = useState(null);

    const [services, setServices] = useState([]);
    const [servicesSelected, setServicesSelected] = useState([]);

    const [displayFormContactDetails, setDisplayFormContactDetails] = useState(false);
    const [personnalInfoFormData, setPersonnalInfoFormData] = useState({
        lastname: "",
        firstname: "",
        email: "",
        phone: ""
    });
    const [errorsPersonnalInfo, setErrorsPersonnalInfo] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    const { t, i18n } = useTranslation();
    const SERENITY_SLUG = 'serenity-pack';

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await Api.getAllServices();
                const res = response.data;
                if (res.status) {
                    setServices(res.data);

                } else {
                    console.log("Error:", res.error);
                }

                if (id) {
                    const response = await Api.getQuotation(id);
                    const res = response.data;

                    if (res.status) {
                        setPersonnalInfoFormData(res.data.customer);
                        setQuotation(res.data);

                    } else {
                        console.log("Error:", res.error);
                    }
                }
            } catch (error) {
                console.error("An error occurred:", error);
            }
        };

        fetchData();
        setLoading(false);

    }, [id]);

    const onSelectService = (value) => {
        setServicesSelected((previousServices) => {
            const serenityPack = previousServices.find(
                (service) => service.slug === SERENITY_SLUG
            );

            const selectedServices = Array.isArray(value)
                ? value.filter((service) => service.slug !== SERENITY_SLUG)
                : [];

            const updatedServices = serenityPack
                ? [...selectedServices, serenityPack]
                : selectedServices;

            return updatedServices;
        });
    };

    const getNights = (q) => {
        const start = _.get(q, 'dates.startDate');
        const end = _.get(q, 'dates.endDate');
        if (!start || !end) return 0;
        const s = new Date(start);
        const e = new Date(end);
        const diff = Math.floor((e.getTime() - s.getTime()) / (1000 * 60 * 60 * 24));
        return Number.isFinite(diff) && diff > 0 ? diff : 0;
    };

    const serenityPricePerDay = React.useMemo(() => {
        const acc = _.get(loadedQuotation, 'prices.typeOfAccommodation');
        if (acc === 'domain') return 400;
        if (acc === 'home' || acc === 'annex') return 200;
        return 0; // unknown / none
    }, [loadedQuotation]);

    // Is serenity currently selected?
    const isSerenityAdded = servicesSelected.some(s => s.slug === SERENITY_SLUG);

    useEffect(() => {
        if (!isSerenityAdded) return;
        setServicesSelected(prev =>
            prev.map(s =>
                s.slug === SERENITY_SLUG
                    ? {
                        ...s,
                        isPriceDefined: false,
                        price: serenityPricePerDay,
                        VAT: 0,
                        priceMustBePaidEach: 'day',
                        name: s.name ?? { fr: 'Pack Sérénité', en: 'Serenity Pack' },
                        quantity: 1,
                    }
                    : s
            )
        );
    }, [isSerenityAdded, serenityPricePerDay]);

    const upsertSerenity = () => {
        setServicesSelected(prev => {
            const others = prev.filter(s => s.slug !== SERENITY_SLUG);
            return [
                ...others,
                {
                    slug: SERENITY_SLUG,
                    name: { fr: 'Pack Sérénité', en: 'Serenity Pack' },
                    quantity: 1,
                    isPriceDefined: false,
                    price: serenityPricePerDay,
                    VAT: 0,
                    priceMustBePaidEach: 'day',
                },
            ];
        });
    };

    const removeSerenity = () =>
        setServicesSelected(prev => prev.filter(s => s.slug !== SERENITY_SLUG));

    const toggleSerenity = () => {
        if (isSerenityAdded) removeSerenity();
        else upsertSerenity();
    };

    const displayNumberWithSpace = (number) => {
        return String(number).replace(/\B(?=(\d{3})+(?!\d))/g, " ");
    }

    const getPriceService = (service) => {
        if (!service.isPriceDefined) return;
        const unit = Number(service.price || 0);
        const qty = Number(service.quantity || 1);
        let total = unit * qty;

        const nbDays = getNights(loadedQuotation);
        if (service.priceMustBePaidEach === 'day' && nbDays > 0) {
            total *= nbDays;
        } else if (service.priceMustBePaidEach === 'week' && nbDays > 0) {
            total *= Math.max(1, Math.ceil(nbDays / 7));
        }

        return displayNumberWithSpace(total.toFixed(2).replace('.00', ''));
    };

    const getSubTotalServices = () => {
        let totalPrice = 0;
        for (const service of servicesSelected) {
            const priceStr = getPriceService(service);
            if (priceStr === undefined) return "devis";
            const numeric = Number(String(priceStr).replace(/\s/g, '').replace(',', '.'));
            totalPrice += isNaN(numeric) ? 0 : numeric;
        }
        // pretty print
        return displayNumberWithSpace(totalPrice.toFixed(2).replace('.00', ''));
    };

    const clickDisplayFormContactDetails = () => {
        if (!servicesSelected.length) {
            return Helper.toast(
                t('errors.noServicesSelected'),
                "error"
            );
        }

        setDisplayFormContactDetails(true);

        setTimeout(() => {
            document
                .getElementById('quotation-contact-form')
                ?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start'
                });
        }, 100);
    };

    const updatePersonnalInfoFormData = (newData) => { setPersonnalInfoFormData(newData) };

    async function sendQuotation() {
        if (isSubmitting) return; // Prevent multiple clicks

        setIsSubmitting(true); // Disable button

        const tmpErrorsPersonnalInfo = Validator.validatePersonnalInfoQuotationFormData(personnalInfoFormData, t);
        setErrorsPersonnalInfo(tmpErrorsPersonnalInfo);

        if (Object.keys(tmpErrorsPersonnalInfo).length === 0) {
            const quotation = {
                dates: {
                    startDate: null,
                    endDate: null,
                    creationDate: new Date(),
                    expirationDate: new Date(new Date().setMonth(new Date().getMonth() + 1))
                },
                mainInfos: {
                    status: 'draft',
                    language: i18n.language,
                    numberOfGuests: {
                        adults: 0,
                        childs: 0
                    },
                    comment: _.get(loadedQuotation, 'mainInfos.quotationNumber', null) ?
                        "Lié au devis " + _.get(loadedQuotation, 'mainInfos.quotationNumber', null) : ""
                },
                prices: {
                    accommodationPrice: 0,
                    typeOfAccommodation: 'none',
                    totalPrice: Number(String(getSubTotalServices().replace(/\s/g, ''))),
                    cleaning: 0,
                    discountLongAccommodation: 0,
                    touristTax: 0,
                    enableDeposit: false
                },
                customer: {
                    lastname: personnalInfoFormData.lastname,
                    firstname: personnalInfoFormData.firstname,
                    email: personnalInfoFormData.email,
                    phone: personnalInfoFormData.phone
                },
                guests: [],
                address: {},
                services: servicesSelected.map(service => ({
                    slug: service.slug,
                    name: service.name['fr'],
                    quantity: service.quantity,
                    isPriceDefined: service.isPriceDefined,
                    price: service.isPriceDefined ? Number(service.price || 0) : 0,
                    VAT: service.VAT,
                    priceMustBePaidEach: service.priceMustBePaidEach
                })),
                history: []
            };

            try {
                const response = await Api.saveQuotation(quotation);
                Helper.handleApiResponse(response, t('responses.quotationSend'), t('responses.quotationError'));
                setDisplayFormContactDetails(false);

                // const responseEmail = await Api.sendQuotation(quotation);
                // Helper.handleApiResponse(responseEmail, t('responses.quotationSent'), t('responses.quotationError'));

                if (_.get(response, 'data.status')) {
                    window.dataLayer = window.dataLayer || [];
                    window.dataLayer.push({
                        event: 'quotationFormSubmissionSuccess',
                        formType: 'quotation'
                    });
                }
            } catch (error) {
                console.error("Error sending quotation:", error);
            } finally {
                setIsSubmitting(false); // Re-enable button after request is done
            }
        } else {
            setIsSubmitting(false); // Reset button if validation fails
        }
    }

    return (
        <div className='bg-backgroundColor'>
            <Helmet>
                <title>{t('pageQuotation.page')}</title>
            </Helmet>

            <header className="flex justify-center pt-8">
                <LogoLong width={250} height={64} />
                <div className='absolute top-[15px] right-[15px]'>
                    <I18nComponentChooseLanguage />
                </div>
            </header>

            <div className='m-auto max-w-5xl pt-14'>
                {loading ? (
                    <Loading />
                ) : (
                    <>
                        <TitleQuotation title={t('pageQuotation.services.welcomeMessage')} subTitle={t('pageQuotation.services.subTitle')} description={t('pageQuotation.services.description')} fullWidth={true} />

                        <div className="relative w-full mx-auto flex flex-col md:flex-row gap-6 md:gap-10 mb-10">
                            <div className="w-full md:w-3/5 py-6 px-4 md:px-8 h-fit">
                                <SerenityPackCard
                                    pricePerDay={serenityPricePerDay}
                                    onToggle={toggleSerenity}
                                    isAdded={isSerenityAdded}
                                    disabled={serenityPricePerDay === 0}
                                />

                                <ListOfServices services={services} numberOfGuests={_.get(loadedQuotation, 'mainInfos.numberOfGuests.adults', 12) + _.get(loadedQuotation, 'mainInfos.numberOfGuests.childs', 10)} onChange={onSelectService} />
                            </div>

                            <div className="w-full md:w-2/5 h-auto mt-10 flex flex-col">
                                <div
                                    className={`
        bg-white h-fit flex flex-col justify-end
        ${displayFormContactDetails ? 'relative' : 'sticky top-10'}
    `}
                                >
                                    <div className="py-6 px-4 md:px-6 shadow-lg flex flex-col justify-between min-h-[500px]">
                                        <div>
                                            <div className="text-left">
                                                <H3 title={t('pageQuotation.services.summary')} fontWeight='normal' />
                                            </div>

                                            <Hr />

                                            {servicesSelected.length > 0 &&
                                                <>
                                                    {servicesSelected.map((service, index) => (
                                                        <div className="flex justify-between" key={index}>
                                                            <span className="text-sm">
                                                                {service.slug === SERENITY_SLUG
                                                                    ? (service.name[i18n.language] || service.name.en)
                                                                    : (<>{service.quantity}x&nbsp;{service.name[i18n.language] || service.name.en}</>)
                                                                }
                                                            </span>
                                                            <span className="text-sm">
                                                                {!service.isPriceDefined || getPriceService(service) === "0"
                                                                    ? t('pageQuotation.services.toDefine')
                                                                    : `${getPriceService(service)}€`
                                                                }
                                                            </span>
                                                        </div>
                                                    ))}
                                                </>
                                            }
                                        </div>

                                        <div>
                                            <div className="flex mt-20 justify-between">
                                                <H3 title={t('pageQuotation.services.total')} />
                                                <span>{getSubTotalServices() === "devis" ? t('pageQuotation.services.toDefine') : getSubTotalServices() + "€"}</span>
                                            </div>

                                            {!displayFormContactDetails &&
                                                <div className="mt-6 text-center w-full">
                                                    <Button onClick={clickDisplayFormContactDetails} text={t('pageQuotation.services.getQuotation')} className="w-full" />
                                                </div>
                                            }

                                            {displayFormContactDetails && (
                                                <div id="quotation-contact-form">
                                                    <Hr />

                                                    <DescriptionSplit
                                                        description={t('pageQuotation.services.descriptionContactForm')}
                                                        className="text-center mb-8"
                                                    />

                                                    <PersonnalInfoForm
                                                        customer={personnalInfoFormData}
                                                        errors={errorsPersonnalInfo}
                                                        updatePersonnalInfoFormData={updatePersonnalInfoFormData}
                                                        fullWidth={true}
                                                    />

                                                    <Button
                                                        onClick={sendQuotation}
                                                        text={t('pageQuotation.services.validate')}
                                                        disabled={isSubmitting}
                                                        className="mt-8 w-full"
                                                    />
                                                </div>
                                            )}

                                            <div className="mt-4 text-xs text-center">
                                                {t('pageQuotation.services.contactFinalizeQuotation')}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}